import { createSlice } from '@reduxjs/toolkit'
import { notification } from 'antd'
import { Auth } from 'aws-amplify'
import { API } from 'src/utils/interceptor'
import Sentry from 'src/utils/sentry'
import { updateCreditsNotes } from './OrdersSlice'
import { setDataPlusOrders } from './DataplusIntagrationSlice'
import {
  RejectedNotification,
  SuccessNotification,
} from '../notifications/SuccessNotification'
import moment from 'moment'
import { getPriceChangeActor } from 'src/utils/getPriceChangeActor'
import {
  GROWTH_ENGINE_GUIDED_LANE_REASONS,
  emitGrowthEngineGuidedLaneProgress,
} from 'src/utils/growthEngineNudgeRefresh'
import { buildPostGrRematchVisibilityNotice } from 'src/utils/invoiceMatchRefreshHelpers'
import {
  getXeroAutoSendEligibility,
  planXeroExportJobs,
  XERO_AUTO_SEND_SKIP_REASON,
} from 'src/utils/xeroAutoSendBill'
import { resolveXeroOrganizationId } from 'src/utils/xeroOrganization'
import { getLinkedInvoicesForReceiptingOrder } from 'src/utils/reconciliationHealth'
import { enrichReceiptingRowsWithNominalCodes } from 'src/utils/enrichReceiptingRowsWithNominalCodes'
import { pickActionableXeroClientMessage } from 'src/utils/xeroExportErrors'
import {
  OLLIE_FINDINGS_WRITE_EVENTS,
  ollieFindingsSaved,
} from 'src/utils/ollieFindingsRefresh'

async function rematchInvoicesAfterGrSave(venueId, purchaseOrderNumber) {
  if (!venueId || !purchaseOrderNumber) return null
  try {
    const result = await API.post(
      'api2',
      `invoices/rematch-for-po/${venueId}`,
      {
        body: { purchaseOrderNumbers: [purchaseOrderNumber] },
      },
    )
    const notice = buildPostGrRematchVisibilityNotice(result)
    if (notice) {
      notification.warning({
        message: notice.message,
        description: notice.description,
        duration: 5,
      })
    }
    return result
  } catch (error) {
    console.error('Post-GR invoice rematch failed', error)
    Sentry.captureException(error)
    const notice = buildPostGrRematchVisibilityNotice(null, error)
    notification.warning({
      message: notice.message,
      description: notice.description,
      duration: 5,
    })
    return null
  }
}

const initialState = {
  receiptingOrders: [],
  voidedReceiptingOrders: [],
  filterDates: {
    start: moment().subtract(40, 'days').valueOf(),
    end: moment().valueOf(),
  },
  /**
   * Which date the GR range filter uses.
   * orderedDate = existing default (do not change silently).
   * actualDeliveryDate = physical arrival (explicit month-end delivery filter).
   */
  filterDateBasis: 'orderedDate',
  receiptingOrdersAlreadyExists: null,
  receiptingProducts: [],
  isLoading: false,
  receiptingLoading: false,
  venueSupplier: null,
  approvedReceipting: [],
  notApprovedReceipting: [],
  supplierProducts: [],
  updatingReceiptingProduct: false,
  history: [],
  totalCount: 0,
  pagination: { page: 1, limit: 50 },
  historyLoading: false,
  createdReceipting: [],
  orderedProductReceiptings: [],
  error: '',
  xeroProducts: [],
  isSavingReconciliation: false,
  isSavingCnRequest: false,
}

const receiptingSlice = createSlice({
  name: 'receipting',
  initialState,
  reducers: {
    setReceiptingOrders: (state, action) => {
      state.receiptingOrders = action.payload
    },
    setVoidedReceiptingOrders: (state, action) => {
      state.voidedReceiptingOrders = action.payload
    },
    setFilterDates: (state, action) => {
      state.filterDates = action.payload
    },
    setFilterDateBasis: (state, action) => {
      const next = action.payload
      state.filterDateBasis =
        next === 'actualDeliveryDate' ? 'actualDeliveryDate' : 'orderedDate'
    },
    setReceiptingExist: (state, action) => {
      state.receiptingOrdersAlreadyExists = action.payload
    },
    /**
     * Mark GR order list stale so the next Goods Receipting visit refetches.
     * Keeps current rows in memory so a slow refresh does not flash empty.
     */
    invalidateReceiptingOrdersCache: state => {
      state.receiptingOrdersAlreadyExists = null
    },
    setReceiptingProducts: (state, action) => {
      const index = state.receiptingProducts.findIndex(order => {
        return (
          order.supplier === action.payload.supplier &&
          order.purchaseOrderNumber === action.payload.purchaseOrderNumber
        )
      })
      if (index > -1) {
        state.receiptingProducts[index] = action.payload
      } else {
        state.receiptingProducts.push(action.payload)
      }
    },
    updateReceiptingLineHasVat: (state, action) => {
      const {
        supplierId,
        purchaseOrderNumber,
        orderId,
        hasVat,
        vatTreatment,
        priceIncludesVat,
      } = action.payload
      state.receiptingProducts = state.receiptingProducts.map(bundle => {
        const bundleSupplierId = bundle?.supplier?._id || bundle?.supplier
        if (
          String(bundleSupplierId) !== String(supplierId) ||
          String(bundle.purchaseOrderNumber) !== String(purchaseOrderNumber)
        ) {
          return bundle
        }
        return {
          ...bundle,
          orders: (bundle.orders || []).map(line =>
            String(line._id) === String(orderId)
              ? {
                  ...line,
                  hasVat: Boolean(hasVat),
                  vatTreatment: vatTreatment || line.vatTreatment,
                  priceIncludesVat: Boolean(priceIncludesVat),
                }
              : line,
          ),
        }
      })
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setReceiptingLoading: (state, action) => {
      state.receiptingLoading = action.payload
    },
    setUpdatingReceiptingProduct: (state, action) => {
      state.updatingReceiptingProduct = action.payload
    },
    updateReceiptingOrder: (state, action) => {
      const {
        supplierId,
        PONumber,
        savedBy,
        receiptingStatus,
        totalSpend,
        supplierDiscountPennies,
        supplierDiscountNote,
        totalSpendReceivedBeforeDiscount,
        retrospectivelyEdited,
        actualDeliveryDate,
        // display-only VAT pair for the Receipted amount column
        totalSpendReceivedVatExPennies,
        totalSpendReceivedVatIncPennies,
      } = action.payload
      state.receiptingOrders = state?.receiptingOrders?.map(item => {
        return item.supplier._id === supplierId &&
          item.purchaseOrderNumber === PONumber
          ? {
              ...item,
              savedBy,
              receiptingStatus,
              ...(totalSpend != null
                ? { totalSpend, totalSpendReceived: totalSpend }
                : {}),
              ...(supplierDiscountPennies != null
                ? { supplierDiscountPennies }
                : {}),
              ...(supplierDiscountNote != null ? { supplierDiscountNote } : {}),
              ...(totalSpendReceivedBeforeDiscount != null
                ? { totalSpendReceivedBeforeDiscount }
                : {}),
              ...(totalSpendReceivedVatExPennies != null
                ? { totalSpendReceivedVatExPennies }
                : {}),
              ...(totalSpendReceivedVatIncPennies != null
                ? { totalSpendReceivedVatIncPennies }
                : {}),
              ...(retrospectivelyEdited === true
                ? { retrospectivelyEdited: true }
                : {}),
              ...(actualDeliveryDate ? { actualDeliveryDate } : {}),
            }
          : item
      })
    },
    updateTotalSpendOrders: (state, action) => {
      const { supplierId, PONumber, totalSpend } = action.payload
      state.receiptingOrders = state.receiptingOrders.map(item => {
        return item.supplier._id === supplierId &&
          item.purchaseOrderNumber === PONumber
          ? { ...item, totalSpend: item.totalSpend + totalSpend }
          : item
      })
    },
    removeOldOrders: (state, action) => {
      const { supplierId, PONumber } = action.payload
      state.receiptingProducts = state.receiptingProducts.filter(item => {
        return (
          item.supplier !== supplierId && item.purchaseOrderNumber !== PONumber
        )
      })
    },
    setChecklistOrders: (state, action) => {
      const { venueSupplier, filteredChanges } = action.payload
      state.venueSupplier = venueSupplier
      const notApproved = []
      const approved = []
      filteredChanges.forEach(item => {
        item.receipting.supplierApproved !== undefined
          ? approved.push(item)
          : notApproved.push(item)
      })
      state.approvedReceipting = approved
      state.notApprovedReceipting = notApproved
    },
    handleChangeOrder: (state, action) => {
      const updatedOrder = action.payload
      state.notApprovedReceipting = state.notApprovedReceipting.map(order => {
        return order._id !== updatedOrder._id ? order : updatedOrder
      })
    },
    setSupplierProducts: (state, action) => {
      state.supplierProducts = action.payload
    },
    setReceiptingHistory: (state, action) => {
      state.history = action.payload
    },
    setHistoryLoading: (state, action) => {
      state.historyLoading = action.payload
    },
    setTotalCount: (state, action) => {
      state.totalCount = action.payload
    },
    setPagination: (state, action) => {
      state.pagination = action.payload
    },
    setCreatedReceipting: (state, action) => {
      state.createdReceipting = action.payload
    },
    setOrderedProductReceiptings: (state, action) => {
      state.orderedProductReceiptings = action.payload
    },
    setXeroProducts: (state, action) => {
      state.xeroProducts = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
    },
    setSavingReconciliation: (state, action) => {
      state.isSavingReconciliation = action.payload
    },
    setSavingCnRequest: (state, action) => {
      state.isSavingCnRequest = action.payload
    },
  },
})

export const {
  setOrders,
  setReceiptingOrders,
  setVoidedReceiptingOrders,
  setReceiptingProducts,
  updateReceiptingLineHasVat,
  setLoading,
  setReceiptingLoading,
  updateReceiptingOrder,
  setChecklistOrders,
  handleChangeOrder,
  removeOldOrders,
  setSupplierProducts,
  setReceiptingHistory,
  setHistoryLoading,
  setTotalCount,
  setPagination,
  setCreatedReceipting,
  setOrderedProductReceiptings,
  setUpdatingReceiptingProduct,
  setError,
  setFilterDates,
  setFilterDateBasis,
  setReceiptingExist,
  invalidateReceiptingOrdersCache,
  setXeroProducts,
  setSavingReconciliation,
  setSavingCnRequest,
} = receiptingSlice.actions

async function attachWeightReceiptNotes(venueId, orders) {
  const rows = Array.isArray(orders) ? orders : []
  const purchaseOrderNumbers = [
    ...new Set(
      rows
        .map(order => String(order?.purchaseOrderNumber || '').trim())
        .filter(Boolean),
    ),
  ]
  if (!venueId || !purchaseOrderNumbers.length) return rows
  try {
    const result = await API.get(
      'api2',
      `receipting/${venueId}/weight-receipts?purchaseOrderNumbers=${encodeURIComponent(
        purchaseOrderNumbers.join(','),
      )}`,
    )
    const notes = new Map(
      (result?.notes || []).map(note => [
        String(note?.purchaseOrderNumber || '').trim(),
        String(note?.label || '').trim(),
      ]),
    )
    const flagged = new Set([
      ...(result?.purchaseOrderNumbers || []),
      ...notes.keys(),
    ])
    return rows.map(order => {
      const purchaseOrderNumber = String(
        order?.purchaseOrderNumber || '',
      ).trim()
      return {
        ...order,
        receiptedByWeight: flagged.has(purchaseOrderNumber),
        weightReceiptLabel: notes.get(purchaseOrderNumber) || '',
      }
    })
  } catch (error) {
    console.error('Weight receipt notes failed', error)
    Sentry.captureException(error)
    return rows
  }
}

export const fetchReceiptingOrders =
  (venueId, start, end) => async (dispatch, getState) => {
    try {
      dispatch(setLoading(true))
      const normalizedStart = start.split('/').reverse().join('-')
      const normalizedEnd = end.split('/').reverse().join('-')
      const dateBasis =
        getState()?.receipting?.filterDateBasis === 'actualDeliveryDate'
          ? 'actualDeliveryDate'
          : 'orderedDate'
      const basePath = `venue/${venueId}/receipting/orders?start=${normalizedStart}&end=${normalizedEnd}&dateBasis=${dateBasis}`
      const [receiptingOrders, voidedReceiptingOrders] = await Promise.all([
        API.get('api', basePath),
        API.get('api', `${basePath}&voidedOnly=true`).catch(error => {
          console.error('Error fetching voided receipting orders:', error)
          Sentry.captureException(error)
          return []
        }),
      ])

      const withWeightNotes = await attachWeightReceiptNotes(
        venueId,
        receiptingOrders || [],
      )

      dispatch(setReceiptingOrders(withWeightNotes))
      dispatch(
        setVoidedReceiptingOrders(
          Array.isArray(voidedReceiptingOrders) ? voidedReceiptingOrders : [],
        ),
      )

      const filteredOrdersForDataplus = (receiptingOrders || []).filter(
        order => order.isOrderSavedToDataplus,
      )
      dispatch(setDataPlusOrders(filteredOrdersForDataplus))
      dispatch(setReceiptingExist(venueId))
      dispatch(setLoading(false))
    } catch (e) {
      dispatch(setLoading(false))
      console.error(e)
      Sentry.captureException(e)
      return false
    }
  }

export const fetchReceiptingProducts =
  (venueId, supplierId, PONumber) => async dispatch => {
    try {
      dispatch(setReceiptingLoading(true))
      const receiptingProducts = await API.get(
        'api',
        `venue/${venueId}/receipting/products?supplierId=${supplierId}&PONumber=${PONumber}`,
      )
      dispatch(setReceiptingProducts(receiptingProducts))
      dispatch(setReceiptingLoading(false))
      return receiptingProducts // Return the fetched data
    } catch (e) {
      dispatch(setLoading(false))
      console.error(e)
      Sentry.captureException(e)
      return false
    }
  }
export const fetchXeroProducts =
  (venueId, supplierId, PONumber) => async dispatch => {
    try {
      dispatch(setReceiptingLoading(true))
      const receiptingProducts = await API.get(
        'api',
        `venue/${venueId}/receipting/products?supplierId=${supplierId}&PONumber=${PONumber}`,
      )
      dispatch(setXeroProducts(receiptingProducts.orders))
      dispatch(setReceiptingLoading(false))
      return receiptingProducts.orders
    } catch (e) {
      dispatch(setLoading(false))
      console.error(e)
      Sentry.captureException(e)
      return false
    }
  }

export const createNewReceipting = (data, updateInfo) => async dispatch => {
  try {
    const { saveType } = updateInfo
    const changedBy = await getPriceChangeActor()
    // Retrospective receipt editing: stamp control flags on every line —
    // the server re-validates role + receipted state before any write.
    const retroStamp = updateInfo.retrospectiveEdit
      ? {
          retrospectiveEdit: true,
          username: updateInfo.retroEditUsername || changedBy,
        }
      : {}
    const payload = data.map(item => ({ ...item, changedBy, ...retroStamp }))

    // Set appropriate loading state based on save type
    if (saveType === 'cn_request') {
      dispatch(setSavingCnRequest(true))
    } else {
      dispatch(setSavingReconciliation(true))
    }

    const { venueId, supplierId, PONumber, supplierDiscount } = updateInfo

    // Persist PO-level supplier discount before line GR create so rematch
    // after save sees the adjusted receipted total.
    // Skip on retrospective edits: express locks purchase-order-adjustment
    // after the PO is receipted (PO_ADJUSTMENT_LOCKED). Discount stays
    // historical; only line qty/price/status are corrected.
    const shouldPersistSupplierDiscount =
      !updateInfo.retrospectiveEdit &&
      venueId &&
      PONumber &&
      supplierDiscount &&
      typeof supplierDiscount.discountPennies === 'number'

    if (shouldPersistSupplierDiscount) {
      const adjustmentResponse = await API.put(
        'api2',
        `receipting/${venueId}/purchase-order-adjustment`,
        {
          body: {
            purchaseOrderNumber: PONumber,
            supplier: supplierId,
            discountPennies: Math.max(
              0,
              Math.round(supplierDiscount.discountPennies),
            ),
            note: supplierDiscount.note || '',
            rematch: false,
          },
        },
      )
      if (!adjustmentResponse?.success) {
        throw new Error(
          adjustmentResponse?.message ||
            'Could not save supplier discount on this purchase order',
        )
      }
    }

    const arr = await API.post('api', `venue/receipting/create`, {
      body: payload,
    })
    dispatch(setCreatedReceipting(arr))
    ollieFindingsSaved({
      venueId,
      event: OLLIE_FINDINGS_WRITE_EVENTS.DELIVERY_RECEIPTED,
      viaServer: true,
    })
    if (updateInfo?.venueId) {
      emitGrowthEngineGuidedLaneProgress(
        updateInfo.venueId,
        GROWTH_ENGINE_GUIDED_LANE_REASONS.PO_RECEIPTED,
      )
    }
    dispatch(removeOldOrders(updateInfo))
    dispatch(fetchReceiptingProducts(venueId, supplierId, PONumber))

    // Step 5/8 — refresh Matching for open invoices on this PO before the
    // post-save Match offer (strong toast vs open Match / picker).
    // Chunk 19: soft-toast rematch errors / cap — never fail GR save.
    if (venueId && PONumber) {
      await rematchInvoicesAfterGrSave(venueId, PONumber)
    }

    // Clear loading state
    if (saveType === 'cn_request') {
      dispatch(setSavingCnRequest(false))
    } else {
      dispatch(setSavingReconciliation(false))
    }
  } catch (e) {
    // Clear loading state on error
    if (updateInfo.saveType === 'cn_request') {
      dispatch(setSavingCnRequest(false))
    } else {
      dispatch(setSavingReconciliation(false))
    }
    console.error(e)
    Sentry.captureException(e)
    const apiMessage =
      e?.response?.data?.message || e?.response?.data?.error || e?.message
    RejectedNotification({
      message: 'Could not save receipt',
      description: apiMessage || 'Please try again.',
    })
    return false
  }
}

export const upsertPurchaseOrderSupplierDiscount =
  ({
    venueId,
    purchaseOrderNumber,
    supplierId,
    discountPennies,
    note = '',
    rematch = true,
  }) =>
  async () => {
    try {
      const response = await API.put(
        'api2',
        `receipting/${venueId}/purchase-order-adjustment`,
        {
          body: {
            purchaseOrderNumber,
            supplier: supplierId,
            discountPennies: Math.max(
              0,
              Math.round(Number(discountPennies) || 0),
            ),
            note: note || '',
            rematch,
          },
        },
      )
      if (!response?.success) {
        throw new Error(response?.message || 'Could not save supplier discount')
      }
      return response.adjustment
    } catch (e) {
      console.error(e)
      Sentry.captureException(e)
      const apiMessage =
        e?.response?.data?.message || e?.response?.data?.error || e?.message
      RejectedNotification({
        message: 'Could not save supplier discount',
        description: apiMessage || 'Please try again.',
      })
      return false
    }
  }
export const deletedOrder =
  (supplierId, PONumber, venueId) => async (dispatch, getState) => {
    try {
      await API.del(
        'api',
        `receipting/deleteOrder?supplierId=${supplierId}&PONumber=${PONumber}`,
      )
      ollieFindingsSaved({
        venueId,
        event: OLLIE_FINDINGS_WRITE_EVENTS.DELIVERY_RECEIPTED,
        viaServer: true,
      })
      const { filterDates } = getState().receipting
      const normalizedStart = moment(filterDates.start).format('YYYY-MM-DD')
      const normalizedEnd = moment(filterDates.end).format('YYYY-MM-DD')
      await dispatch(
        fetchReceiptingOrders(venueId, normalizedStart, normalizedEnd),
      )
    } catch (e) {
      console.error('Error during delete and fetch process:', e)
      Sentry.captureException(e)
      const message =
        e?.response?.data?.message ||
        e?.message ||
        'This order can’t be deleted because it’s receipted or has an invoice.'
      RejectedNotification({
        message: 'Couldn’t delete order',
        description: message,
      })
      return false
    }
  }

/** Chunk B2–B3 — soft-void a PO (all lines). Auto-unlink + rematch on server/client. */
export const voidReceiptingOrder =
  ({
    supplierId,
    purchaseOrderNumber,
    voidReason,
    voidNote = '',
    voidedBy = '',
    voidedFromReceiptingStatus = '',
    venueId,
  }) =>
  async (dispatch, getState) => {
    try {
      const result = await API.post('api', 'receipting/voidOrder', {
        body: {
          supplierId,
          purchaseOrderNumber,
          voidReason,
          voidNote,
          voidedBy,
          voidedFromReceiptingStatus,
        },
      })
      ollieFindingsSaved({
        venueId,
        event: OLLIE_FINDINGS_WRITE_EVENTS.DELIVERY_RECEIPTED,
        viaServer: true,
      })
      const { filterDates } = getState().receipting
      const normalizedStart = moment(filterDates.start).format('YYYY-MM-DD')
      const normalizedEnd = moment(filterDates.end).format('YYYY-MM-DD')
      await dispatch(
        fetchReceiptingOrders(venueId, normalizedStart, normalizedEnd),
      )

      // B3: refresh Matching so unlinked invoices return to unmatched / needs matching.
      const unlinkedIds = Array.isArray(result?.unlinkedInvoiceIds)
        ? result.unlinkedInvoiceIds
        : []
      if (unlinkedIds.length && venueId) {
        const { rematchInvoiceMatching, getUploadedInvoices } = await import(
          './NewInvoicesSlice'
        )
        await Promise.all(
          unlinkedIds.map(id =>
            rematchInvoiceMatching(String(id)).catch(rematchError => {
              console.error('Post-void rematch failed', rematchError)
              Sentry.captureException(rematchError)
            }),
          ),
        )
        await dispatch(getUploadedInvoices(venueId))
      }

      SuccessNotification({
        message: result?.alreadyVoided
          ? 'Order already voided'
          : 'Order voided',
        description:
          result?.message ||
          'Removed from Goods receipting and live spend. Kept for audit.',
      })
      return result
    } catch (e) {
      console.error('Error during void order:', e)
      Sentry.captureException(e)
      const message =
        e?.response?.data?.message || e?.message || 'Couldn’t void this order.'
      RejectedNotification({
        message: 'Couldn’t void order',
        description: message,
      })
      return false
    }
  }

/** Chunk B4 — restore a voided PO within the 7-day undo window. */
export const undoVoidReceiptingOrder =
  ({ supplierId, purchaseOrderNumber, venueId }) =>
  async (dispatch, getState) => {
    try {
      const result = await API.post('api', 'receipting/undoVoidOrder', {
        body: {
          supplierId,
          purchaseOrderNumber,
        },
      })
      ollieFindingsSaved({
        venueId,
        event: OLLIE_FINDINGS_WRITE_EVENTS.DELIVERY_RECEIPTED,
        viaServer: true,
      })
      const { filterDates } = getState().receipting
      const normalizedStart = moment(filterDates.start).format('YYYY-MM-DD')
      const normalizedEnd = moment(filterDates.end).format('YYYY-MM-DD')
      await dispatch(
        fetchReceiptingOrders(venueId, normalizedStart, normalizedEnd),
      )
      SuccessNotification({
        message: result?.alreadyRestored
          ? 'Order already restored'
          : 'Order restored',
        description:
          result?.message || 'This order is back on Goods receipting.',
      })
      return result
    } catch (e) {
      console.error('Error during undo void order:', e)
      Sentry.captureException(e)
      const message =
        e?.response?.data?.message ||
        e?.message ||
        'Couldn’t undo void for this order.'
      RejectedNotification({
        message: 'Couldn’t undo void',
        description: message,
      })
      return false
    }
  }

export const fetchChecklist =
  (venueId, supplierId, PONumber) => async dispatch => {
    try {
      dispatch(setLoading(true))
      const data = await API.get(
        'api',
        `receipting/${venueId}/${supplierId}/${encodeURIComponent(
          PONumber,
        )}/checklist`,
      )
      dispatch(setChecklistOrders(data))
      dispatch(setLoading(false))
      return data
    } catch (e) {
      dispatch(setLoading(false))
      console.error(e)
      Sentry.captureException(e)
      throw e
    }
  }

export const postChecklist = (data, venueId) => async dispatch => {
  try {
    dispatch(setReceiptingLoading(true))

    // Transform data to include the changeRequestAction field for backend
    const transformedData = data.map(order => ({
      ...order,
      receipting: {
        ...order.receipting,
        // Ensure changeRequestAction is properly set
        changeRequestAction:
          order.receipting.changeRequestAction ||
          (order.receipting.supplierApproved === true
            ? 'approve_credit'
            : order.receipting.supplierApproved === false
            ? 'reject_request'
            : null),
        // Maintain backward compatibility
        supplierApproved:
          order.receipting.supplierApproved !== undefined
            ? order.receipting.supplierApproved
            : order.receipting.changeRequestAction !== 'reject_request',
      },
    }))

    const result = await API.post('api', `receipting/checklist`, {
      body: transformedData,
    })
    ollieFindingsSaved({
      venueId,
      event: OLLIE_FINDINGS_WRITE_EVENTS.CREDIT_UPDATED,
      viaServer: true,
    })
    dispatch(setReceiptingLoading(false))
    return result
  } catch (e) {
    console.log(e)
    dispatch(setReceiptingLoading(false))
    throw e // Re-throw the error so it can be caught in the component
  }
}

export const fetchSupplierProducts =
  (venueId, supplierId) => async dispatch => {
    try {
      dispatch(setLoading(true))
      const products = await API.get(
        'api',
        `venue/${venueId}/${supplierId}/products`,
      )
      dispatch(setSupplierProducts(products))
      dispatch(setLoading(false))
    } catch (e) {
      dispatch(setLoading(false))
      console.error(e)
      Sentry.captureException(e)
      return false
    }
  }

export const addProduct = data => async (dispatch, getState) => {
  try {
    const {
      venue: venueId,
      supplier: supplierId,
      purchaseOrderNumber: PONumber,
    } = data
    dispatch(setLoading(true))
    await API.post('api', 'receipting/addproduct', {
      body: data,
    })
    ollieFindingsSaved({
      venueId,
      event: OLLIE_FINDINGS_WRITE_EVENTS.DELIVERY_RECEIPTED,
      viaServer: true,
    })

    // Refresh Matching for open invoices on this PO so totals/match see the new line.
    // Chunk 19: soft-toast rematch errors / cap — never fail add-product.
    if (venueId && PONumber) {
      await rematchInvoicesAfterGrSave(venueId, PONumber)
    }

    dispatch(removeOldOrders({ supplierId, PONumber }))
    await dispatch(fetchReceiptingProducts(venueId, supplierId, PONumber))

    const { filterDates } = getState().receipting
    if (venueId && filterDates?.start && filterDates?.end) {
      const normalizedStart = moment(filterDates.start).format('YYYY-MM-DD')
      const normalizedEnd = moment(filterDates.end).format('YYYY-MM-DD')
      await dispatch(
        fetchReceiptingOrders(venueId, normalizedStart, normalizedEnd),
      )
    }

    dispatch(setLoading(false))
    return true
  } catch (e) {
    dispatch(setLoading(false))
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const getHistory = orderId => async dispatch => {
  try {
    dispatch(setHistoryLoading(true))
    const history = await API.get('api', `receipting/${orderId}/history`)
    dispatch(setReceiptingHistory(history))
    dispatch(setHistoryLoading(false))
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    dispatch(setHistoryLoading(false))
    return false
  }
}
export const getFullHistory = (venueId, pagination) => async dispatch => {
  try {
    dispatch(setHistoryLoading(true))
    const result = await API.get(
      'api',
      `receipting/${venueId}/history-all?page=${pagination.page}&limit=${pagination.limit}`,
    )
    dispatch(setReceiptingHistory(result.receiptingArr))
    dispatch(setTotalCount(result.receiptingTotalCount))
    dispatch(setPagination(pagination))
    dispatch(setHistoryLoading(false))
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    dispatch(setHistoryLoading(false))
    return false
  }
}

export const clearFullHistory = () => async dispatch => {
  dispatch(setReceiptingHistory([]))
  dispatch(setPagination({ page: 1, limit: 50 }))
  dispatch(setTotalCount(0))
}

export const fetchReceiptingsForOrderedProducts =
  () => async (dispatch, getState) => {
    const { orders } = getState()
    const receiptingIds = orders.filtered
      .filter(order => order.receipting !== undefined)
      .map(order => order.receipting)
    await API.post('api', `receipting/orderedProducts`, {
      body: { receiptingIds: receiptingIds },
    }).then(data => {
      dispatch(setOrderedProductReceiptings(data))
      return data
    })
  }

export const markOrderDelivered =
  (venueId, supplierId, PONumber, actualDeliveryDate) => async () => {
    try {
      const result = await API.put('api', `venue/receipting/mark-delivered`, {
        body: {
          venueId,
          supplierId,
          PONumber,
          actualDeliveryDate,
        },
      })
      ollieFindingsSaved({
        venueId,
        event: OLLIE_FINDINGS_WRITE_EVENTS.DELIVERY_RECEIPTED,
        viaServer: true,
      })
      return result
    } catch (e) {
      // serverless-offline can expose routes with a stage prefix (/stage/*)
      if (e?.response?.status === 404) {
        try {
          const fallbackResult = await API.put(
            'api',
            `stage/venue/receipting/mark-delivered`,
            {
              body: {
                venueId,
                supplierId,
                PONumber,
                actualDeliveryDate,
              },
            },
          )
          ollieFindingsSaved({
            venueId,
            event: OLLIE_FINDINGS_WRITE_EVENTS.DELIVERY_RECEIPTED,
            viaServer: true,
          })
          return fallbackResult
        } catch (fallbackError) {
          console.error('markOrderDelivered fallback error:', fallbackError)
          Sentry.captureException(fallbackError)
          return false
        }
      }
      console.error('markOrderDelivered error:', e)
      Sentry.captureException(e)
      return false
    }
  }

export const makeOrderResolved = (receiptingId, venueId) => async dispatch => {
  try {
    const updateReceipting = await API.put(
      'api',
      `receipting/credits-history/${receiptingId}/orderResolved`,
    )
    ollieFindingsSaved({
      venueId,
      event: OLLIE_FINDINGS_WRITE_EVENTS.CREDIT_UPDATED,
      viaServer: true,
    })
    dispatch(updateCreditsNotes(updateReceipting))
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const updateReceiptingProduct = (productId, data) => async dispatch => {
  try {
    dispatch(setUpdatingReceiptingProduct(true))
    const changedBy = await getPriceChangeActor()
    const updatedReceiptingProduct = await API.patch(
      'api',
      `product/${productId}`,
      { body: { ...data, changedBy } },
    )

    SuccessNotification({ message: updatedReceiptingProduct.message })

    dispatch(setUpdatingReceiptingProduct(false))
    console.log('updatedReceiptingProduct', updatedReceiptingProduct)
  } catch (e) {
    dispatch(setUpdatingReceiptingProduct(false))
    SuccessNotification({ message: 'Something went wrong!!!' })
    console.log('Error in updateReceiptingProduct', e)
  }
}

export const updateReceiptingOrderHasVat =
  ({ supplierId, purchaseOrderNumber, updates }) =>
  async dispatch => {
    if (!Array.isArray(updates) || updates.length === 0) return

    try {
      dispatch(setUpdatingReceiptingProduct(true))
      const response = await API.patch('api2', 'receipting/orderHasVat', {
        body: {
          data: updates.map(item => ({
            orderId: String(item.orderId),
            hasVat: Boolean(item.hasVat),
            vatTreatment: item.vatTreatment || undefined,
            priceIncludesVat: Boolean(item.hasVat && item.priceIncludesVat),
          })),
        },
      })

      if (!response?.success) {
        throw new Error('Could not update VAT')
      }

      const updated = response?.updated || updates
      updated.forEach(item => {
        dispatch(
          updateReceiptingLineHasVat({
            orderId: item.orderId,
            hasVat: item.hasVat,
            vatTreatment: item.vatTreatment,
            priceIncludesVat: item.priceIncludesVat,
            supplierId,
            purchaseOrderNumber,
          }),
        )
      })

      return { updated, failed: response?.failed || [] }
    } catch (e) {
      const apiMessage =
        e?.response?.data?.message || e?.response?.data?.error || e?.message
      RejectedNotification({
        message: 'Could not update VAT',
        description: apiMessage || 'Please try again.',
      })
      console.log('Error in updateReceiptingOrderHasVat', e)
      Sentry.captureException(e)
      throw e
    } finally {
      dispatch(setUpdatingReceiptingProduct(false))
    }
  }
export const createXeroBill =
  (
    billData,
    files,
    venueId,
    organizationId,
    orderIds,
    selectedInvoice,
    {
      redirectOnSuccess = true,
      notifyOnSuccess = true,
      notifyOnError = true,
      rejectOnError = false,
      forceSend = false,
    } = {},
  ) =>
  async dispatch => {
    try {
      dispatch(setLoading(true))
      const session = await Auth.currentSession()
      const token = session.getAccessToken().getJwtToken()
      const formData = new FormData()
      formData.append('billData', JSON.stringify(billData))
      formData.append('venueId', venueId)
      formData.append('organizationId', organizationId)
      formData.append('orderIds', JSON.stringify(orderIds))
      formData.append('selectedInvoice', selectedInvoice || '')
      if (forceSend) formData.append('forceSend', 'true')
      const filesArray = Array.isArray(files) ? files : files ? [files] : []
      filesArray.forEach(file => {
        formData.append('files', file)
      })

      const response = await fetch(
        `${process.env.REACT_APP_EXPRESS_API_ENDPOINT}venue/createXeroBill`,
        {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: `${token}`,
          },
        },
      )
      const resBody = await response.json().catch(() => ({}))
      if (response.status === 200 && resBody?.alreadySent) {
        dispatch(setLoading(false))
        return {
          success: true,
          alreadySent: true,
          xeroInvoiceId: resBody.xeroInvoiceId,
          userMessage: pickActionableXeroClientMessage(resBody),
        }
      }
      // Part 16 — resume completed post-create steps without recreating the Bill/CN
      if (
        response.status === 200 &&
        resBody?.resumed &&
        resBody?.xeroInvoiceId
      ) {
        dispatch(setLoading(false))
        const resumeMsg = pickActionableXeroClientMessage(resBody)
        if (notifyOnSuccess) {
          if (resBody?.exportIncomplete) {
            RejectedNotification({
              message: 'Xero export incomplete',
              description: resumeMsg,
            })
          } else {
            SuccessNotification({
              message: 'Xero export completed',
              description: resumeMsg,
            })
          }
        }
        return {
          success: true,
          resumed: true,
          exportIncomplete: Boolean(resBody?.exportIncomplete),
          xeroInvoiceId: resBody.xeroInvoiceId,
          xeroExportSteps: resBody?.xeroExportSteps,
          userMessage: resumeMsg,
          canForceSend: false,
        }
      }
      // Honest success: require HTTP 201 AND a real Xero InvoiceID / CreditNoteID
      if (response.status === 201 && resBody?.xeroInvoiceId) {
        dispatch(setLoading(false))
        const successMsg = pickActionableXeroClientMessage(resBody)
        if (notifyOnSuccess) {
          if (resBody?.exportIncomplete) {
            RejectedNotification({
              message: 'Xero export incomplete',
              description: successMsg,
            })
          } else {
            SuccessNotification({
              message: 'Xero Bill created successfully',
              description:
                successMsg || 'Xero Bill has been created successfully.',
            })
          }
        }

        if (redirectOnSuccess && !resBody?.exportIncomplete) {
          setTimeout(() => {
            window.location.href = `/v/${venueId}/purchasing/receipting`
          }, 2000)
        }
        return {
          success: true,
          xeroInvoiceId: resBody.xeroInvoiceId,
          exportIncomplete: Boolean(resBody?.exportIncomplete),
          xeroExportSteps: resBody?.xeroExportSteps,
          userMessage: successMsg,
          canForceSend: false,
        }
      }
      console.error('Error creating Xero Bill:', resBody)
      dispatch(setLoading(false))
      Sentry.captureException(resBody?.error)
      const errorMessage = pickActionableXeroClientMessage(resBody)
      if (notifyOnError) {
        RejectedNotification({
          message: 'Xero export failed',
          description: errorMessage,
        })
      }
      if (rejectOnError) {
        const err = new Error(errorMessage)
        err.canForceSend = Boolean(resBody?.canForceSend)
        err.errorCode = resBody?.errorCode
        throw err
      }
      return {
        success: false,
        error: errorMessage,
        errorCode: resBody?.errorCode,
        canForceSend: Boolean(resBody?.canForceSend),
      }
    } catch (error) {
      dispatch(setLoading(false))
      const errorMessage = pickActionableXeroClientMessage(
        {},
        error?.message || error,
      )
      if (notifyOnError) {
        RejectedNotification({
          message: 'Xero export failed',
          description: errorMessage,
        })
      }
      Sentry.captureException(error)
      console.error('Error creating Xero bill or attaching file:', error)
      if (rejectOnError) throw error
      return {
        success: false,
        error: errorMessage,
        canForceSend: Boolean(error?.canForceSend),
      }
    }
  }

/**
 * Reuse the established createXeroBill endpoint after one-click reconciliation.
 * Eligibility failures are returned quietly; callers add contextual guidance.
 */
export const autoSendReconciledInvoiceToXero =
  ({
    venue,
    order,
    venueSupplier,
    invoiceId,
    invoiceNumber,
    invoice,
    forceSend = false,
  }) =>
  async (dispatch, getState) => {
    const eligibility = getXeroAutoSendEligibility({
      venue,
      order,
      venueSupplier,
    })
    // Allow retry when the order badge says sent but a credit note document
    // on the PO has not been exported yet (dual Bill + CN path).
    const state = getState()
    const uploadedInvoices = state?.newInvoices?.uploadedInvoices || []
    const linkedDocs = getLinkedInvoicesForReceiptingOrder(
      order,
      uploadedInvoices,
    )
    const pendingCredit = linkedDocs.some(
      doc =>
        String(doc?.documentType || '')
          .toLowerCase()
          .includes('credit') && !String(doc?.xeroInvoiceId || '').trim(),
    )
    if (
      !eligibility.eligible &&
      !(
        eligibility.reason === XERO_AUTO_SEND_SKIP_REASON.ALREADY_SENT &&
        pendingCredit
      )
    ) {
      return { sent: false, reason: eligibility.reason }
    }

    const supplierId = order?.supplier?._id || order?.supplierId
    if (!supplierId) {
      return {
        sent: false,
        reason: XERO_AUTO_SEND_SKIP_REASON.MISSING_SUPPLIER,
      }
    }

    const receiptingProductsRaw = await dispatch(
      fetchXeroProducts(venue._id, supplierId, order.purchaseOrderNumber),
    )
    let venueProducts = []
    try {
      const vpResponse = await API.get('api', `venue/${venue._id}/products`)
      venueProducts = Array.isArray(vpResponse)
        ? vpResponse
        : vpResponse?.products || vpResponse?.data || []
    } catch (vpErr) {
      console.warn(
        'Could not load venue products for nominal code enrichment',
        vpErr,
      )
    }
    const receiptingProducts = enrichReceiptingRowsWithNominalCodes(
      Array.isArray(receiptingProductsRaw) ? receiptingProductsRaw : [],
      venueProducts,
      supplierId,
    )
    const organizationId =
      eligibility.organization?.id ||
      resolveXeroOrganizationId(venue.xeroConnection)

    let xeroAccounts = []
    let xeroTaxRates = []
    try {
      // Sequential — same Xero org must not be hit in parallel (429 risk).
      const accountsResponse = await API.get(
        'api2',
        `venue/${venue._id}/xero/accounts?organizationId=${organizationId}`,
      )
      xeroAccounts = accountsResponse?.data || []
      const taxRatesResponse = await API.get(
        'api2',
        `venue/${venue._id}/xero/tax-rates?organizationId=${organizationId}`,
      ).catch(() => ({ data: [] }))
      xeroTaxRates = taxRatesResponse?.data || []
    } catch (accountsError) {
      console.warn(
        'Could not load Xero accounts for product nominal codes',
        accountsError,
      )
    }

    // Prefer the reconciled invoice object; merge linked PO docs for CN split.
    const primaryInvoice =
      invoice ||
      linkedDocs.find(
        doc => String(doc?.id || doc?._id) === String(invoiceId),
      ) ||
      null
    const financeDocuments = linkedDocs.length
      ? linkedDocs
      : primaryInvoice
      ? [primaryInvoice]
      : []

    const plan = planXeroExportJobs({
      financeDocuments,
      venue,
      order,
      venueSupplier,
      receiptingProducts,
      forceSend,
      xeroAccounts,
      xeroTaxRates,
      invoiceNumber,
      invoice: primaryInvoice,
    })

    const createdIds = []
    let lastCanForceSend = false
    let latestBillXeroId = null
    for (const job of plan.jobs) {
      if (
        job.documentType === 'creditNote' &&
        job.billData &&
        latestBillXeroId &&
        !job.billData.allocateToXeroInvoiceId
      ) {
        job.billData.allocateToXeroInvoiceId = latestBillXeroId
      }
      const result = await dispatch(
        createXeroBill(
          job.billData,
          [],
          venue._id,
          organizationId,
          order.orderIds,
          job.selectedInvoiceId || invoiceId || '',
          {
            redirectOnSuccess: false,
            notifyOnSuccess: false,
            notifyOnError: false,
            rejectOnError: true,
            forceSend,
          },
        ),
      )
      lastCanForceSend = Boolean(result?.canForceSend)
      if (result?.success && result?.xeroInvoiceId) {
        createdIds.push(result.xeroInvoiceId)
        if (job.documentType === 'invoice') {
          latestBillXeroId = result.xeroInvoiceId
        }
      } else if (!result?.alreadySent) {
        return {
          sent: createdIds.length > 0,
          reason: null,
          xeroInvoiceId: createdIds[0] || null,
          xeroInvoiceIds: createdIds,
          canForceSend: lastCanForceSend,
          partial: createdIds.length > 0,
        }
      } else if (result?.alreadySent && job.documentType === 'invoice') {
        latestBillXeroId = result.xeroInvoiceId || latestBillXeroId
      }
    }

    return {
      sent: createdIds.length > 0 || plan.jobs.length === 0,
      reason: null,
      xeroInvoiceId: createdIds[0] || null,
      xeroInvoiceIds: createdIds,
      canForceSend: false,
      exportMode: plan.mode,
    }
  }

export default receiptingSlice
